import {
  ContactTypeEnumValues,
  MemberStatusEnumValues,
  OrganizationMemberRoleEnum,
  OrganizationMemberRoleEnumValues,
  OrganizationTypeEnumValues,
} from '@forgd/shared/enums'
import { z } from 'zod'

export const MemberInvitationDto = z.object({
  firstName: z.string().trim().min(1, {
    message: 'First name is required',
  }),
  lastName: z.string().trim().min(1, {
    message: 'Last name is required',
  }),
  position: z.string().optional(),
  email: z.string().toLowerCase().trim().email({ message: 'Email is invalid' }),
  organizationName: z.string().optional(),
  role: z.enum(OrganizationMemberRoleEnumValues).optional().default(OrganizationMemberRoleEnum.Viewer),
})

export const FetchMembershipInvitationByCodeResponseBody = z.object({
  organizationId: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  invitedEmail: z.string().nullish(),
  memberPosition: z.string().nullish(),
  userId: z.string().nullish(),
  status: z.string(),
  projectName: z.string().nullish(),
  organizationType: z.enum(OrganizationTypeEnumValues).nullish(),
  organizationName: z.string().nullish(),
})

export const SendInvitationRequestBody = z.object({
  organizationId: z.string(),
  // this is needed for non-advisor organizations
  projectId: z.string().nullish(),
  members: z.array(MemberInvitationDto),
})

export const ResendInvitationRequestBody = z.object({
  organizationId: z.string(),
  // this is needed for non-advisor organizations
  projectId: z.string().nullish(),
  memberId: z.string(),
})

export const AcceptMemberInvitationDto = MemberInvitationDto.extend({
  contactType: z.enum(ContactTypeEnumValues),
  contact: z.string().min(1),
})

export const AcceptInvitationRequestBody = AcceptMemberInvitationDto.extend({
  code: z.string(),
})

export const RejectInvitationRequestBody = z.object({
  organizationId: z.string().uuid(),
})

export const DeleteMemberBody = z.object({
  organizationId: z.string(),
  // this is needed for non-advisor organizations
  projectId: z.string().nullish(),
  memberId: z.string(),
})

export const OrganizationMemberDetailsBody = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().nullable(),
  organizationId: z.string().uuid(),
  firstName: z.string().trim().min(1).nullable(),
  lastName: z.string().trim().min(1).nullable(),
  email: z.string().email().nullable(),
  position: z.string().nullable(),
  status: z.enum(MemberStatusEnumValues),
  role: z.enum(OrganizationMemberRoleEnumValues),
  isAdvisor: z.boolean(),
  projects: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        ticker: z.string().nullable(),
        image: z.string().nullable(),
        userId: z.string().uuid().nullable(),
      }),
    )
    .optional(),
})

export const GetMembersResponseBody = z.object({
  members: z.array(OrganizationMemberDetailsBody),
  maxSeats: z.number().safe(),
})

export const SendInvitationErrorSchema = z.object({
  email: z.string().email(),
  organizationType: z.enum(OrganizationTypeEnumValues),
})

export type SendInvitationRequestBodyType = z.infer<typeof SendInvitationRequestBody>
export type MemberInvitationDto = z.infer<typeof MemberInvitationDto>
export type AcceptMemberInvitationDto = z.infer<typeof AcceptMemberInvitationDto>
export type OrganizationMemberDetailsBody = z.infer<typeof OrganizationMemberDetailsBody>
export type SendInvitationError = z.infer<typeof SendInvitationErrorSchema>
